/**
 * Tour configurations for all modules.
 * Each module has 7 steps following the standard structure:
 * 1. What this module is for
 * 2. Where to start
 * 3. What info must be captured first
 * 4. What actions follow next
 * 5. Outputs and downstream links
 * 6. Common mistakes to avoid
 * 7. How this links to other modules
 */

const TOUR_CONFIGS = {
  delay_events: {
    moduleId: 'delay_events',
    moduleName: 'Delay Events',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Delay Events',
        description: 'This module tracks events that cause or may cause delays to the construction programme.',
        whatIsThis: 'A register of all delay-causing events on the project, from weather disruptions to late information.',
        whyItMatters: 'Properly recording delay events protects your contractual position and provides evidence for extension of time claims.',
        whatToDo: 'Review the walkthrough to understand how to record, monitor, and escalate delay events correctly.',
      },
      {
        id: 'create', target: '[data-testid="create-event-btn"], [data-testid="add-event-btn"]', roles: ['all'],
        title: 'Create a New Event',
        description: 'Click here to log a new delay event as soon as it occurs or is anticipated.',
        whatIsThis: 'The starting point for recording any event that impacts the programme schedule.',
        whyItMatters: 'Late recording weakens your claim. Events should be logged within 24-48 hours of occurrence.',
        whatToDo: 'Click this button and fill in the event details: date, description, cause category, and affected activities.',
        example: '"Heavy rainfall on 15 Jan caused excavation stoppage for 3 days, affecting Activity A002."',
      },
      {
        id: 'details', target: '[data-testid="event-form"], [data-testid="events-table"]', roles: ['all'],
        title: 'Capture Event Details',
        description: 'Record the essential information: event date, cause category, description, and estimated impact.',
        whatIsThis: 'The core data fields that define each delay event and its impact on the project.',
        whyItMatters: 'Accurate details are critical for notices, claims, and programme recovery planning.',
        whatToDo: 'Fill in: Event date, cause (e.g. Weather, Late Info, Access), description, and estimated delay in days.',
        roleContent: {
          contractor: { whatToDo: 'Record the event, estimate the delay, and prepare to issue a notice to the employer.' },
          consultant: { whatToDo: 'Review submitted events, assess validity, and recommend acceptance or rejection.' },
        },
      },
      {
        id: 'status', target: '[data-testid="event-status"], [data-testid="events-table"] tbody tr:first-child', roles: ['all'],
        title: 'Track Event Status',
        description: 'Monitor each event through its lifecycle: Draft, Active, Under Review, Resolved.',
        whatIsThis: 'The workflow states that track an event from creation to resolution.',
        whyItMatters: 'Keeping statuses current ensures nothing falls through the cracks and deadlines are met.',
        whatToDo: 'Update the status as the event progresses. Move to "Under Review" when submitting for assessment.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs and Downstream Links',
        description: 'Delay events feed into notices, extension of time claims, and programme recovery plans.',
        whatIsThis: 'The connection between delay events and the formal contractual processes they trigger.',
        whyItMatters: 'A delay event without a notice is just a complaint. The formal process protects your rights.',
        whatToDo: 'After recording an event, issue a formal notice (Notices module), then link it to a claim if applicable.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes to Avoid',
        description: 'Learn from frequent errors that weaken delay claims.',
        whatIsThis: 'A checklist of pitfalls that can undermine your delay management process.',
        whyItMatters: 'Avoiding these mistakes significantly improves your success rate in claims and disputes.',
        whatToDo: 'Ensure you: (1) Record events promptly, (2) Always issue a notice, (3) Link to affected programme activities, (4) Attach supporting evidence.',
        example: 'Mistake: Recording a delay event 3 weeks after it happened without supporting documents.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Delay Events connect to Notices, Claims, Programme Management, and Site Instructions.',
        whatIsThis: 'How this module integrates with the rest of the platform.',
        whyItMatters: 'Using linked modules creates an audit trail that strengthens your contractual position.',
        whatToDo: 'After creating a delay event: (1) Issue a Notice, (2) Link to the Programme, (3) Create a Claim if costs are involved.',
      },
    ],
  },

  notices: {
    moduleId: 'notices',
    moduleName: 'Notices',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Notices',
        description: 'This module manages formal contractual notices issued between parties on the project.',
        whatIsThis: 'A register for creating, tracking, and responding to formal notices as required by the contract.',
        whyItMatters: 'Notices are legal requirements. Missing a notice deadline can forfeit your right to claim.',
        whatToDo: 'Understand how to create, issue, and track notices to protect contractual rights.',
      },
      {
        id: 'create', target: '[data-testid="create-notice-btn"]', roles: ['all'],
        title: 'Issue a New Notice',
        description: 'Create a formal notice linked to a delay event or contractual obligation.',
        whatIsThis: 'The action that formally communicates a delay, variation, or contractual matter to the other party.',
        whyItMatters: 'Without a formal notice, you may lose the right to claim time or money under the contract.',
        whatToDo: 'Click to create a notice. Select the notice type, link it to the relevant delay event, and set the response deadline.',
        example: '"Notice of Delay — Event DE-004: Late drawing issuance affecting structural works, 12 working days impact."',
      },
      {
        id: 'capture', target: null, roles: ['all'],
        title: 'Essential Notice Information',
        description: 'Every notice must include: notice type, contract clause reference, linked event, description, and deadline.',
        whatIsThis: 'The mandatory fields that make a notice contractually valid.',
        whyItMatters: 'Incomplete notices can be rejected or challenged by the other party.',
        whatToDo: 'Fill in all required fields. Reference the specific contract clause that gives you the right to issue this notice.',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'Notice Actions',
        description: 'After issuing, track the response: acknowledged, disputed, or no response received.',
        whatIsThis: 'The follow-up workflow after a notice has been issued.',
        whyItMatters: 'Tracking responses ensures you can escalate within contractual time limits.',
        whatToDo: 'Monitor the response deadline. If no response is received, the notice may be deemed accepted under the contract.',
        roleContent: {
          consultant: { whatToDo: 'Review the notice, assess validity against the contract, and issue a response within the required timeframe.' },
        },
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs and Downstream Links',
        description: 'Notices create the contractual basis for claims (time and/or cost) and programme adjustments.',
        whatIsThis: 'What happens after a notice is issued and acknowledged.',
        whyItMatters: 'A valid notice is the foundation for a successful claim submission.',
        whatToDo: 'Once acknowledged, link the notice to a Claim for formal cost/time recovery.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid these frequent notice management errors.',
        whatIsThis: 'Critical pitfalls in the notice process.',
        whyItMatters: 'One missed notice can cost the project significant time and money.',
        whatToDo: 'Never: (1) Issue a notice without a contract clause reference, (2) Miss the notice period deadline, (3) Forget to link to the originating event.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Notices are linked to Delay Events (upstream) and Claims (downstream).',
        whatIsThis: 'The contractual chain: Event triggers Notice, Notice enables Claim.',
        whyItMatters: 'Breaking this chain weakens your contractual position.',
        whatToDo: 'Always create the chain: Delay Event, then Notice, then Claim. Each step builds on the previous.',
      },
    ],
  },

  claims: {
    moduleId: 'claims',
    moduleName: 'Claims',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Claims',
        description: 'Manage extension of time and cost claims arising from delay events and notices.',
        whatIsThis: 'The formal mechanism for recovering additional time or costs caused by compensable events.',
        whyItMatters: 'Claims are how contractors recover costs and time lost due to events beyond their control.',
        whatToDo: 'Learn how to create, substantiate, and track claims through to resolution.',
      },
      {
        id: 'create', target: '[data-testid="create-claim-btn"]', roles: ['all'],
        title: 'Submit a New Claim',
        description: 'Create a claim linked to an existing notice and delay event.',
        whatIsThis: 'The formal submission requesting additional time and/or cost compensation.',
        whyItMatters: 'A well-structured claim with proper linkage has a much higher success rate.',
        whatToDo: 'Link the claim to the relevant notice, specify the type (time/cost/both), and attach supporting documentation.',
      },
      {
        id: 'capture', target: null, roles: ['all'],
        title: 'Claim Details to Capture',
        description: 'Include: claim value, time extension requested, detailed narrative, programme impact analysis, and cost breakdown.',
        whatIsThis: 'The substantiation that supports your claim amount and time extension.',
        whyItMatters: 'Under-substantiated claims are routinely rejected or significantly reduced.',
        whatToDo: 'Provide a detailed narrative, attach delay analysis, and include a breakdown of costs with supporting invoices or records.',
        example: '"Claim for 15 days EOT and R2.4M additional costs due to late design information (Notice N-007)."',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'Claim Workflow',
        description: 'Track the claim through: Draft, Submitted, Under Review, Negotiation, Settled, or Rejected.',
        whatIsThis: 'The lifecycle stages of a claim from submission to resolution.',
        whyItMatters: 'Active management through each stage ensures claims are resolved efficiently.',
        whatToDo: 'Update the status as the claim progresses. Add negotiation notes and settlement terms when agreed.',
        roleContent: {
          consultant: { whatToDo: 'Review the claim submission, assess entitlement, quantify the reasonable amount, and issue a determination.' },
        },
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Settled claims result in programme adjustments, payment certificate amendments, and contract value updates.',
        whatIsThis: 'The downstream effects of a resolved claim on the project.',
        whyItMatters: 'Properly recording claim outcomes ensures accurate project financials and programme status.',
        whatToDo: 'After settlement, update the programme with the approved time extension and process the cost through billing.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid weak claims by following proper process.',
        whatIsThis: 'Frequent errors that lead to claim rejection.',
        whyItMatters: 'A rejected claim means unrecovered costs and no time extension.',
        whatToDo: 'Ensure: (1) Notice was issued first, (2) Claim is submitted within contract deadlines, (3) Full supporting documentation is attached, (4) Programme impact is clearly demonstrated.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Claims link upstream to Notices and Delay Events, and downstream to Billing and Programme Management.',
        whatIsThis: 'The full contractual chain from event to financial resolution.',
        whyItMatters: 'Each link in the chain must be documented to withstand scrutiny in disputes.',
        whatToDo: 'Verify all links are in place: Delay Event linked to Notice linked to Claim linked to payment.',
      },
    ],
  },

  rfis: {
    moduleId: 'rfis',
    moduleName: 'Requests for Information (RFIs)',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to RFIs',
        description: 'Manage requests for information between the contractor, consultants, and client.',
        whatIsThis: 'A formal system for requesting and tracking design clarifications, missing information, and technical queries.',
        whyItMatters: 'Unanswered RFIs are the leading cause of construction delays and rework.',
        whatToDo: 'Learn how to create, track, and close RFIs efficiently.',
      },
      {
        id: 'create', target: '[data-testid="create-rfi-btn"]', roles: ['all'],
        title: 'Create a New RFI',
        description: 'Submit a request for information to the relevant consultant or client.',
        whatIsThis: 'A formal request that documents what information is needed and why.',
        whyItMatters: 'Formal RFIs create a paper trail that protects you if the missing information causes delays.',
        whatToDo: 'Click to create an RFI. Specify the subject, discipline, question, and required response date.',
        example: '"RFI-023: Please confirm the steel grade specification for Level 5 transfer beam — drawing S-105 shows Grade 300W but spec calls for Grade 350W."',
      },
      {
        id: 'capture', target: null, roles: ['all'],
        title: 'Essential RFI Information',
        description: 'Include: subject, detailed question, affected drawings/specs, discipline, priority, and response deadline.',
        whatIsThis: 'The information that makes an RFI clear and actionable for the respondent.',
        whyItMatters: 'Vague RFIs get vague answers. Precise RFIs get fast, accurate responses.',
        whatToDo: 'Be specific about what you need clarified. Reference exact drawing numbers and specification clauses.',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'RFI Workflow',
        description: 'Track the RFI through: Draft, Issued, Awaiting Response, Responded, Closed.',
        whatIsThis: 'The lifecycle of an RFI from creation to resolution.',
        whyItMatters: 'Overdue RFIs must be escalated. Unanswered RFIs can trigger delay events.',
        whatToDo: 'Monitor response deadlines. If overdue, escalate and consider raising a delay event.',
        roleContent: {
          consultant: { whatToDo: 'Review the RFI, provide a clear written response, and attach any revised drawings if applicable.' },
        },
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'RFI responses may trigger drawing revisions, specification amendments, variation orders, or delay events.',
        whatIsThis: 'The downstream actions that may result from an RFI response.',
        whyItMatters: 'An RFI response that changes scope should be captured as a variation order.',
        whatToDo: 'After receiving a response, check if it changes scope (create a VO), affects the programme (create a delay event), or requires new drawings.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid these RFI pitfalls.',
        whatIsThis: 'Frequent errors in the RFI process.',
        whyItMatters: 'Poor RFI management leads to costly rework and unrecoverable delays.',
        whatToDo: 'Never: (1) Ask multiple unrelated questions in one RFI, (2) Accept verbal responses without documentation, (3) Continue work without an RFI response on critical items.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'RFIs connect to Drawing Register, Variation Orders, Delay Events, and Site Instructions.',
        whatIsThis: 'How RFIs integrate with the broader project management system.',
        whyItMatters: 'Linking RFIs to related modules creates a complete audit trail of project decisions.',
        whatToDo: 'Link RFIs to affected drawings. If the response changes scope, create a VO. If it causes delay, raise a Delay Event.',
      },
    ],
  },

  site_instructions: {
    moduleId: 'site_instructions',
    moduleName: 'Site Instructions',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Site Instructions',
        description: 'Track formal instructions issued to and from site parties regarding the works.',
        whatIsThis: 'A register of all formal instructions that direct changes, clarifications, or actions on site.',
        whyItMatters: 'Undocumented site instructions are the root cause of many scope disputes and claims.',
        whatToDo: 'Learn how to record, track, and close site instructions properly.',
      },
      {
        id: 'create', target: '[data-testid="create-si-btn"]', roles: ['all'],
        title: 'Issue a Site Instruction',
        description: 'Create a formal record of an instruction given or received on site.',
        whatIsThis: 'The formal documentation of a directive that affects the works.',
        whyItMatters: 'Without documentation, verbal instructions cannot be claimed for and may be denied later.',
        whatToDo: 'Record: who gave the instruction, to whom, what was instructed, and the date.',
        example: '"SI-015: Instructed by Architect to change cladding colour from Grey to Charcoal on North Elevation — verbal instruction confirmed in writing."',
      },
      {
        id: 'capture', target: null, roles: ['all'],
        title: 'What to Capture',
        description: 'Record: instruction details, issuing party, receiving party, date, cost impact, and time impact.',
        whatIsThis: 'The fields that document the instruction for contractual purposes.',
        whyItMatters: 'Complete records enable accurate cost recovery and programme adjustment.',
        whatToDo: 'Always note whether the SI has cost or time implications, even if the amount is not yet known.',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'SI Workflow',
        description: 'Track through: Draft, Issued, Acknowledged, Completed, Disputed.',
        whatIsThis: 'The status progression of a site instruction.',
        whyItMatters: 'Tracking acknowledgement ensures the receiving party cannot later deny receiving the instruction.',
        whatToDo: 'Update the status as the instruction is acted upon. If disputed, document the reasons.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'SIs with cost implications should be linked to Variation Orders. Time impacts should trigger Delay Events.',
        whatIsThis: 'The downstream contractual actions triggered by site instructions.',
        whyItMatters: 'An SI that changes scope without a VO means the contractor absorbs the cost.',
        whatToDo: 'Assess each SI: does it change scope? (Create a VO) Does it delay the programme? (Create a Delay Event)',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid undocumented instructions and missing cost assessments.',
        whatIsThis: 'Frequent SI process failures.',
        whyItMatters: 'Every mistake represents potential unrecovered costs.',
        whatToDo: 'Always: (1) Confirm verbal instructions in writing within 24 hours, (2) Assess cost/time impact immediately, (3) Link to a VO if scope changes.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'SIs link to Variation Orders, Delay Events, RFIs, and Programme Management.',
        whatIsThis: 'The integration points between site instructions and other project management functions.',
        whyItMatters: 'Proper linking ensures no instruction falls through the cracks.',
        whatToDo: 'For each SI, check: Does it need a VO? A Delay Event? An RFI for clarification? A programme update?',
      },
    ],
  },

  drawings: {
    moduleId: 'drawings',
    moduleName: 'Drawing Register',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Drawing Register',
        description: 'Manage all project drawings, revisions, and distribution tracking.',
        whatIsThis: 'A central register of all construction drawings with version control and distribution tracking.',
        whyItMatters: 'Using outdated drawings is one of the most expensive mistakes in construction. This register prevents it.',
        whatToDo: 'Learn how to upload, track revisions, and manage drawing distribution.',
      },
      {
        id: 'upload', target: '[data-testid="upload-drawing-btn"], [data-testid="add-drawing-btn"]', roles: ['all'],
        title: 'Upload a Drawing',
        description: 'Add new drawings or upload revised versions of existing drawings.',
        whatIsThis: 'The process for adding drawings to the project register.',
        whyItMatters: 'Centralised drawing management ensures everyone works from the latest revision.',
        whatToDo: 'Upload the drawing file, enter the drawing number, title, discipline, and revision number.',
      },
      {
        id: 'details', target: null, roles: ['all'],
        title: 'Drawing Details',
        description: 'Capture: drawing number, title, discipline, revision, status, and issue date.',
        whatIsThis: 'The metadata that identifies and categorises each drawing.',
        whyItMatters: 'Accurate metadata makes drawings searchable and ensures the correct version is used on site.',
        whatToDo: 'Fill in all fields. Set the status: For Review, Approved, Superseded. Add discipline tags for filtering.',
      },
      {
        id: 'revisions', target: null, roles: ['all'],
        title: 'Revision Management',
        description: 'Track all drawing revisions with dates, changes summary, and superseded versions.',
        whatIsThis: 'The version history of each drawing showing what changed between revisions.',
        whyItMatters: 'Revision tracking provides evidence of what information was available at any point in time.',
        whatToDo: 'When uploading a revision, note what changed. The previous revision is automatically superseded.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Drawing revisions may trigger RFIs for clarification and should be linked to affected SIs and VOs.',
        whatIsThis: 'The downstream effects of drawing changes.',
        whyItMatters: 'A drawing revision that changes scope is a potential variation order.',
        whatToDo: 'Review each new revision for scope changes. Raise an RFI if the revision introduces ambiguity.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid using outdated drawings and poor distribution tracking.',
        whatIsThis: 'Critical errors in drawing management.',
        whyItMatters: 'Building from the wrong revision is costly and may require demolition and reconstruction.',
        whatToDo: 'Always: (1) Check the register for the latest revision, (2) Distribute new revisions promptly, (3) Mark superseded drawings clearly.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Drawings link to RFIs, Site Instructions, Shop Drawings, and Variation Orders.',
        whatIsThis: 'How drawing management integrates with other modules.',
        whyItMatters: 'Linked records create a full audit trail of design decisions and changes.',
        whatToDo: 'Link drawings to related RFIs (clarifications), SIs (instructions), and VOs (scope changes).',
      },
    ],
  },

  shop_drawings: {
    moduleId: 'shop_drawings',
    moduleName: 'Shop Drawing Tracker',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Shop Drawing Tracker',
        description: 'Track subcontractor shop drawing submissions through the approval cycle.',
        whatIsThis: 'A tracker for managing shop drawing packages from submission through consultant review to approval.',
        whyItMatters: 'Delayed shop drawing approvals directly delay fabrication and installation, impacting the programme.',
        whatToDo: 'Learn the approval workflow and how to manage shop drawing packages efficiently.',
      },
      {
        id: 'create', target: '[data-testid="create-package-btn"]', roles: ['all'],
        title: 'Create a Package',
        description: 'Add a new shop drawing package for a specific trade or discipline.',
        whatIsThis: 'A package groups related shop drawings for a trade (e.g. Structural Steel, Aluminium Windows).',
        whyItMatters: 'Organised packages make it easier to track approval progress and identify bottlenecks.',
        whatToDo: 'Create a package for each trade. Add the subcontractor, discipline, and required approval date.',
      },
      {
        id: 'submission', target: null, roles: ['all'],
        title: 'Submit for Review',
        description: 'Submit the package to the consultant for review with all required drawings attached.',
        whatIsThis: 'The formal submission of shop drawings for consultant review and approval.',
        whyItMatters: 'Incomplete submissions cause review cycles to restart, wasting weeks.',
        whatToDo: 'Attach all drawings, specs, and calculations. Set the submission date and expected review duration.',
        example: '"Package SD-012: Aluminium curtain wall shop drawings — submitted to Facade Consultant for review."',
      },
      {
        id: 'review', target: null, roles: ['all'],
        title: 'Review Cycle',
        description: 'Track the review: Approved, Approved with Comments, Rejected, or Revise & Resubmit.',
        whatIsThis: 'The consultant review outcomes and resubmission tracking.',
        whyItMatters: 'Multiple review cycles delay fabrication. Clear tracking highlights chronic review bottlenecks.',
        whatToDo: 'Record the review outcome. If rejected, note the reasons and track the resubmission.',
        roleContent: {
          consultant: { whatToDo: 'Review drawings against the design intent, mark up comments, and issue the review status within the agreed timeframe.' },
        },
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Approved shop drawings clear fabrication to proceed. Delays may trigger delay events.',
        whatIsThis: 'What happens after the review cycle.',
        whyItMatters: 'Late approvals directly delay fabrication and installation on the critical path.',
        whatToDo: 'Once approved, update the package status. If the review was late, consider raising a Delay Event.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid incomplete submissions and not tracking review durations.',
        whatIsThis: 'Frequent errors in shop drawing management.',
        whyItMatters: 'Each mistake adds days or weeks to the approval cycle.',
        whatToDo: 'Always: (1) Submit complete packages, (2) Track review duration, (3) Follow up on overdue reviews, (4) Log delay events for late approvals.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Shop Drawings link to Drawing Register, RFIs, Delay Events, and Programme Management.',
        whatIsThis: 'The integration between shop drawings and other modules.',
        whyItMatters: 'Linking ensures late approvals are properly recorded as delays and tracked in the programme.',
        whatToDo: 'Link approved shop drawings to the Drawing Register. Link delayed approvals to Delay Events.',
      },
    ],
  },

  programs: {
    moduleId: 'programs',
    moduleName: 'Programme Management',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Programme Management',
        description: 'Upload, analyse, and track construction programme schedules.',
        whatIsThis: 'The central hub for managing the project programme/schedule, tracking progress, and submitting updates to clients.',
        whyItMatters: 'The programme is the backbone of every construction project. Accurate tracking prevents surprises.',
        whatToDo: 'Learn how to upload programmes, analyse them with data files, and submit progress updates.',
      },
      {
        id: 'upload', target: '[data-testid="upload-program-btn"]', roles: ['all'],
        title: 'Upload a Programme',
        description: 'Upload your programme as a PDF or image file.',
        whatIsThis: 'The initial programme upload that creates a record in the system.',
        whyItMatters: 'The uploaded programme becomes the reference document for progress tracking.',
        whatToDo: 'Click to upload a PDF or image of the programme schedule.',
      },
      {
        id: 'data-file', target: '[data-testid^="upload-data-"]', roles: ['all'],
        title: 'Upload a Data File',
        description: 'Upload an XML, Excel, or CSV data file for deterministic schedule analysis.',
        whatIsThis: 'The data file that enables automatic extraction of activities, durations, floats, and progress.',
        whyItMatters: 'XML provides 100% accurate extraction. This is far more reliable than PDF-based analysis.',
        whatToDo: 'Click "Upload Data" and select your XML (P6/MSP), Excel, or CSV schedule export.',
        example: '"Upload the P6 XML export to extract all 700+ activities with exact float and progress values."',
      },
      {
        id: 'analysis', target: null, roles: ['all'],
        title: 'Programme Analysis',
        description: 'Once a data file is uploaded, the system automatically analyses activities, critical path, and float.',
        whatIsThis: 'The deterministic parsing of your schedule data to extract key metrics.',
        whyItMatters: 'Accurate analysis enables meaningful progress reporting and critical path monitoring.',
        whatToDo: 'After uploading the data file, view the analysis results in the programme details modal.',
      },
      {
        id: 'submit-update', target: '[data-testid^="submit-update-"]', roles: ['all'],
        title: 'Submit Progress Update',
        description: 'Send a professional progress report to clients with AI-generated narrative analysis.',
        whatIsThis: 'A formal progress update comparing the current programme against the baseline.',
        whyItMatters: 'Regular progress updates maintain client confidence and document programme status.',
        whatToDo: 'Click "Submit Update", select recipients, and the system generates a professional PDF report with narrative analysis.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid outdated baselines and incomplete data file uploads.',
        whatIsThis: 'Frequent programme management errors.',
        whyItMatters: 'Inaccurate programme data leads to poor decision-making and missed deadlines.',
        whatToDo: 'Always: (1) Upload a data file for accurate analysis, (2) Keep the baseline current, (3) Submit regular progress updates, (4) Use XML format when possible.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Programmes link to Delay Events, Claims, IRS Schedule, and Procurement Schedule.',
        whatIsThis: 'How programme management integrates with schedule-dependent modules.',
        whyItMatters: 'The programme drives procurement timing, IRS requirements, and delay impact assessments.',
        whatToDo: 'Ensure programme updates are reflected in IRS and Procurement schedules.',
      },
    ],
  },

  irs: {
    moduleId: 'irs',
    moduleName: 'Information Required Schedule (IRS)',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to IRS',
        description: 'Track all information required from consultants, with required-by dates linked to the programme.',
        whatIsThis: 'A schedule of all design information, approvals, and decisions needed to keep the programme on track.',
        whyItMatters: 'Late information is the most common cause of construction delays. The IRS provides early warning.',
        whatToDo: 'Understand how to create IRS items, track their status, and escalate overdue items.',
      },
      {
        id: 'items', target: null, roles: ['all'],
        title: 'IRS Items',
        description: 'Each item represents a piece of information needed by a specific date.',
        whatIsThis: 'Individual information requirements with responsible parties and deadlines.',
        whyItMatters: 'Tracking each item ensures nothing is forgotten and all parties know what is expected of them.',
        whatToDo: 'Create items for every piece of information needed. Set the required-by date based on the programme.',
        example: '"Structural steel connection details required by 15 Feb 2026 to allow steel fabrication to start on time."',
      },
      {
        id: 'status', target: null, roles: ['all'],
        title: 'IRS Status Tracking',
        description: 'Track items through: Not Requested, Requested, Received, Outstanding, Late.',
        whatIsThis: 'The lifecycle of each information requirement.',
        whyItMatters: 'Late or outstanding items are early warning signs of potential programme delays.',
        whatToDo: 'Mark items as "Requested" when sent. Update to "Received" when information arrives. Flag "Late" items for escalation.',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'Actions for Overdue Items',
        description: 'Overdue items should be escalated through formal channels.',
        whatIsThis: 'The escalation process for late information.',
        whyItMatters: 'Early escalation can prevent delays from materialising.',
        whatToDo: 'For overdue items: (1) Send a formal reminder, (2) Raise an RFI, (3) If still outstanding, create a Delay Event.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Overdue IRS items feed into Delay Events and may support extension of time claims.',
        whatIsThis: 'What happens when information is late.',
        whyItMatters: 'Documented IRS tracking is powerful evidence in delay claims.',
        whatToDo: 'Use the IRS register as evidence when claiming for late information delays.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid incomplete IRS items and not linking to programme activities.',
        whatIsThis: 'Frequent IRS management errors.',
        whyItMatters: 'An incomplete IRS undermines your ability to demonstrate that delays were caused by late information.',
        whatToDo: 'Always: (1) Link IRS items to programme activities, (2) Set realistic required-by dates, (3) Formally request information in writing.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'IRS links to Programme Management, RFIs, Delay Events, and Notices.',
        whatIsThis: 'How the IRS integrates with other modules.',
        whyItMatters: 'The IRS is the early warning system that feeds into the formal delay management process.',
        whatToDo: 'Link overdue IRS items to RFIs (formal request) and Delay Events (if programme is impacted).',
      },
    ],
  },

  procurement: {
    moduleId: 'procurement',
    moduleName: 'Procurement Schedule',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Procurement',
        description: 'Track material and equipment procurement with lead times linked to the programme.',
        whatIsThis: 'A schedule of all procurement items with order dates, lead times, and required-on-site dates.',
        whyItMatters: 'Late procurement is a common cause of delays. Tracking lead times prevents surprises.',
        whatToDo: 'Learn how to manage procurement items and monitor delivery timelines.',
      },
      {
        id: 'items', target: null, roles: ['all'],
        title: 'Procurement Items',
        description: 'Each item represents a material or equipment package that needs to be ordered.',
        whatIsThis: 'Individual procurement requirements with suppliers, lead times, and delivery dates.',
        whyItMatters: 'Missing a procurement deadline can delay the entire project if the item is on the critical path.',
        whatToDo: 'Add all major procurement items. Set required-on-site dates based on the programme.',
      },
      {
        id: 'status', target: null, roles: ['all'],
        title: 'Order Status',
        description: 'Track items through: Not Ordered, Ordered, In Transit, Delivered, Late.',
        whatIsThis: 'The procurement lifecycle from initial requirement to delivery on site.',
        whyItMatters: 'Real-time status tracking enables proactive management of supply chain risks.',
        whatToDo: 'Update statuses as procurement progresses. Flag items at risk of late delivery.',
      },
      {
        id: 'lead-times', target: null, roles: ['all'],
        title: 'Lead Time Management',
        description: 'Monitor lead times and adjust order dates to ensure timely delivery.',
        whatIsThis: 'The time between ordering and receiving a material or equipment item.',
        whyItMatters: 'Underestimated lead times are a frequent cause of programme delays.',
        whatToDo: 'Research actual lead times from suppliers. Add buffer where appropriate. Monitor delivery progress.',
        example: '"Structural steel: 12-week lead time. Must be ordered by 1 March to meet programme requirement of 24 May."',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Late deliveries trigger Delay Events. Procurement status feeds into programme progress reports.',
        whatIsThis: 'What happens when procurement items are delayed or delivered.',
        whyItMatters: 'Documenting procurement delays strengthens delay claims and supports programme recovery planning.',
        whatToDo: 'For late items: create a Delay Event. For delivered items: confirm receipt and close the item.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid late ordering and not verifying supplier lead times.',
        whatIsThis: 'Frequent procurement errors.',
        whyItMatters: 'Each day of late delivery can cost thousands in standing time and programme delay.',
        whatToDo: 'Always: (1) Order early with confirmed lead times, (2) Track delivery milestones, (3) Have contingency plans for critical items.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Procurement links to Programme Management, Delay Events, and IRS.',
        whatIsThis: 'How procurement integrates with other modules.',
        whyItMatters: 'Procurement timing is driven by the programme and impacts all downstream activities.',
        whatToDo: 'Ensure procurement dates align with the programme. Link late deliveries to Delay Events.',
      },
    ],
  },

  meeting_minutes: {
    moduleId: 'meeting_minutes',
    moduleName: 'Meeting Minutes',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Meeting Minutes',
        description: 'Upload meeting minutes and let AI extract key information automatically.',
        whatIsThis: 'A system for storing, analysing, and tracking action items from project meetings.',
        whyItMatters: 'Meetings generate decisions and action items. Without tracking, commitments are forgotten.',
        whatToDo: 'Learn how to upload minutes and leverage AI extraction for efficient tracking.',
      },
      {
        id: 'upload', target: '[data-testid="upload-minutes-btn"]', roles: ['all'],
        title: 'Upload Minutes',
        description: 'Upload meeting minutes in PDF format for AI analysis.',
        whatIsThis: 'The process of adding meeting minutes to the system.',
        whyItMatters: 'Centralised storage with AI analysis saves hours of manual review.',
        whatToDo: 'Upload the PDF of the meeting minutes. The AI will automatically extract key information.',
      },
      {
        id: 'ai-extraction', target: null, roles: ['all'],
        title: 'AI Extraction',
        description: 'The system automatically extracts: Executive Summary, Key Decisions, Risks, and Action Items.',
        whatIsThis: 'AI-powered analysis that identifies critical information from meeting minutes.',
        whyItMatters: 'Automated extraction ensures nothing important is missed and saves manual review time.',
        whatToDo: 'Review the AI extraction results. Verify accuracy and add any missed items manually.',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'Action Item Tracking',
        description: 'Track extracted action items with due dates and responsible parties.',
        whatIsThis: 'The system for monitoring commitments made in meetings.',
        whyItMatters: 'Untracked action items lead to missed deadlines and broken commitments.',
        whatToDo: 'Review action items, assign responsible parties, set due dates, and follow up at the next meeting.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Action items can be converted to RFIs, Delay Events, or Tasks.',
        whatIsThis: 'How meeting outcomes feed into the project management workflow.',
        whyItMatters: 'Converting meeting actions to formal records ensures they are tracked and completed.',
        whatToDo: 'For each action item, decide: Is it an RFI? A delay risk? A task? Convert it to the appropriate module.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid untracked action items and incomplete minutes records.',
        whatIsThis: 'Frequent meeting management errors.',
        whyItMatters: 'Lost meeting records and untracked actions undermine project coordination.',
        whatToDo: 'Always: (1) Upload minutes within 48 hours, (2) Review AI extraction for accuracy, (3) Convert actions to formal records.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Minutes link to RFIs, Delay Events, Tasks, and Site Instructions.',
        whatIsThis: 'How meeting minutes integrate with other modules.',
        whyItMatters: 'Meetings generate actions that must be formally tracked in the appropriate modules.',
        whatToDo: 'After each meeting: create RFIs for queries, delay events for risks, and tasks for actions.',
      },
    ],
  },

  variation_orders: {
    moduleId: 'variation_orders',
    moduleName: 'Variation Orders',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Variation Orders',
        description: 'Manage scope changes, pricing, and approval of variation orders.',
        whatIsThis: 'A system for tracking changes to the contract scope, from identification through approval to implementation.',
        whyItMatters: 'Unmanaged variations erode project margins and cause disputes.',
        whatToDo: 'Learn the VO lifecycle from creation to settlement.',
      },
      {
        id: 'create', target: '[data-testid="create-vo-btn"]', roles: ['all'],
        title: 'Create a Variation Order',
        description: 'Initiate a VO for any change to the contracted scope of works.',
        whatIsThis: 'The formal record of a proposed change to the contract.',
        whyItMatters: 'Undocumented changes cannot be recovered. Every scope change must be a formal VO.',
        whatToDo: 'Create a VO with: description of change, reason, estimated cost, and time impact.',
      },
      {
        id: 'pricing', target: null, roles: ['all'],
        title: 'Pricing and Quotation',
        description: 'Capture the cost breakdown: labour, materials, plant, subcontractors, and overheads.',
        whatIsThis: 'The financial quantification of the variation.',
        whyItMatters: 'Accurate pricing ensures fair compensation and prevents margin erosion.',
        whatToDo: 'Prepare a detailed cost breakdown. Attach supplier quotations and rate references.',
      },
      {
        id: 'approval', target: null, roles: ['all'],
        title: 'Approval Workflow',
        description: 'Track through: Draft, Submitted, Under Review, Recommended, Approved, Rejected.',
        whatIsThis: 'The multi-stage approval process for variation orders.',
        whyItMatters: 'Proper approval prevents disputes about authorisation and payment.',
        whatToDo: 'Submit for technical review, then commercial approval. Track each stage and follow up on delays.',
        roleContent: {
          consultant: { whatToDo: 'Review the VO technically, verify quantities, and recommend approval or rejection to the client.' },
        },
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Approved VOs update the contract value, may adjust the programme, and feed into payment certificates.',
        whatIsThis: 'The downstream effects of an approved variation order.',
        whyItMatters: 'Approved VOs directly impact the project bottom line and completion date.',
        whatToDo: 'After approval: update the contract value, adjust the programme if time was granted, and include in the next payment certificate.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid starting work before VO approval and poor cost documentation.',
        whatIsThis: 'Critical VO management errors.',
        whyItMatters: 'Working without approval risks non-payment. Poor documentation invites disputes.',
        whatToDo: 'Never: (1) Start VO work without written approval, (2) Submit without a detailed cost breakdown, (3) Forget to claim time extension if applicable.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'VOs link to Site Instructions, RFIs, Claims, Programme, and Billing.',
        whatIsThis: 'How variation orders connect to other modules.',
        whyItMatters: 'Each VO should trace back to its origin (SI, RFI) and forward to its financial impact (billing).',
        whatToDo: 'Link VOs to originating SIs/RFIs. Update the programme for time impact. Include in billing.',
      },
    ],
  },

  tasks: {
    moduleId: 'tasks',
    moduleName: 'Task Management',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Tasks',
        description: 'Manage project tasks, assignments, and deadlines across your team.',
        whatIsThis: 'A task management system for tracking day-to-day project activities and team assignments.',
        whyItMatters: 'Clear task assignment and tracking ensures accountability and timely completion.',
        whatToDo: 'Learn how to create, assign, and track tasks effectively.',
      },
      {
        id: 'create', target: '[data-testid="create-task-btn"]', roles: ['all'],
        title: 'Create a Task',
        description: 'Add a new task with a clear description, assignee, and due date.',
        whatIsThis: 'An actionable item that needs to be completed by a specific person by a specific date.',
        whyItMatters: 'Without clear task definition, work falls through the cracks.',
        whatToDo: 'Set: title, description, assignee, priority, due date, and any linked module items.',
      },
      {
        id: 'assignment', target: null, roles: ['all'],
        title: 'Task Assignment',
        description: 'Assign tasks to team members with clear expectations and deadlines.',
        whatIsThis: 'The process of delegating work to specific team members.',
        whyItMatters: 'Ambiguous assignments lead to duplication of effort or work not being done at all.',
        whatToDo: 'Assign to a specific person (not a group). Set a realistic due date. Add context and instructions.',
      },
      {
        id: 'tracking', target: null, roles: ['all'],
        title: 'Progress Tracking',
        description: 'Track tasks through: To Do, In Progress, Done.',
        whatIsThis: 'The task lifecycle from creation to completion.',
        whyItMatters: 'Real-time tracking enables managers to identify bottlenecks early.',
        whatToDo: 'Update task status as work progresses. Mark overdue tasks for attention.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Completed tasks contribute to project progress and may close RFIs, SIs, or other items.',
        whatIsThis: 'How task completion connects to the broader project workflow.',
        whyItMatters: 'Linking tasks to module items ensures that formal records are updated when work is completed.',
        whatToDo: 'When closing a task linked to an RFI or SI, update the linked item status as well.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid vague tasks and missing deadlines.',
        whatIsThis: 'Frequent task management errors.',
        whyItMatters: 'Vague tasks are never completed. Missing deadlines affects the entire team.',
        whatToDo: 'Always: (1) Be specific in task descriptions, (2) Set achievable deadlines, (3) Follow up on overdue tasks.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Tasks can be created from Meeting Minutes, RFIs, and any other module.',
        whatIsThis: 'How tasks integrate with the broader platform.',
        whyItMatters: 'Creating tasks from other modules ensures action items are tracked to completion.',
        whatToDo: 'Use the "Create Task" action from other modules to link tasks to their source.',
      },
    ],
  },

  correspondence: {
    moduleId: 'correspondence',
    moduleName: 'Correspondence',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Correspondence',
        description: 'Manage all project correspondence and communication logs.',
        whatIsThis: 'A centralised log of all project emails, letters, and communications.',
        whyItMatters: 'A complete correspondence record is essential evidence in disputes and audits.',
        whatToDo: 'Learn how to log, categorise, and search project correspondence.',
      },
      {
        id: 'log', target: null, roles: ['all'],
        title: 'Log Correspondence',
        description: 'Record incoming and outgoing project communications.',
        whatIsThis: 'The process of adding correspondence to the project log.',
        whyItMatters: 'Unlogged correspondence cannot be referenced in disputes or claims.',
        whatToDo: 'Log all significant project communications: emails, letters, meeting notes, and verbal confirmations.',
      },
      {
        id: 'categorise', target: null, roles: ['all'],
        title: 'Categorise and Tag',
        description: 'Apply categories, tags, and links to make correspondence searchable and traceable.',
        whatIsThis: 'The metadata that makes correspondence easy to find and reference.',
        whyItMatters: 'In disputes, you need to find specific correspondence quickly. Good categorisation enables this.',
        whatToDo: 'Tag by subject, discipline, and urgency. Link to related RFIs, SIs, or events.',
      },
      {
        id: 'actions', target: null, roles: ['all'],
        title: 'Actions from Correspondence',
        description: 'Extract action items and create RFIs, Delay Events, or Tasks from correspondence.',
        whatIsThis: 'Converting correspondence into formal project management records.',
        whyItMatters: 'Important instructions buried in emails must be formalised to be enforceable.',
        whatToDo: 'Review correspondence for: scope changes (create VO), instructions (create SI), queries (create RFI), delay risks (create Delay Event).',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Outputs',
        description: 'Correspondence feeds into the project audit trail and supports formal records.',
        whatIsThis: 'How logged correspondence supports other project management functions.',
        whyItMatters: 'A complete communication trail strengthens your position in any dispute.',
        whatToDo: 'Ensure all formal actions have supporting correspondence linked.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid incomplete logging and not linking to formal records.',
        whatIsThis: 'Frequent correspondence management errors.',
        whyItMatters: 'Missing correspondence weakens your contractual position.',
        whatToDo: 'Always: (1) Log all significant correspondence same-day, (2) Link to relevant modules, (3) Follow up on unanswered items.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Correspondence links to RFIs, SIs, Delay Events, VOs, and Claims.',
        whatIsThis: 'How correspondence integrates with formal project records.',
        whyItMatters: 'Each formal record should trace back to its originating correspondence.',
        whatToDo: 'When creating formal records, always link the relevant correspondence as supporting evidence.',
      },
    ],
  },

  reports: {
    moduleId: 'reports',
    moduleName: 'Reports',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Reports',
        description: 'Generate comprehensive project reports across all modules.',
        whatIsThis: 'A reporting engine that aggregates data from all project modules into professional reports.',
        whyItMatters: 'Regular reporting keeps stakeholders informed and provides early warning of issues.',
        whatToDo: 'Learn how to generate, customise, and distribute project reports.',
      },
      {
        id: 'types', target: null, roles: ['all'],
        title: 'Report Types',
        description: 'Generate reports by module: RFI Register, Delay Event Log, VO Schedule, and more.',
        whatIsThis: 'The different report formats available for each module.',
        whyItMatters: 'Different stakeholders need different views of the project data.',
        whatToDo: 'Select the report type, set the date range, and choose the visibility level.',
      },
      {
        id: 'filters', target: null, roles: ['all'],
        title: 'Filters and Date Ranges',
        description: 'Customise reports with filters for status, date range, and priority.',
        whatIsThis: 'Controls that let you focus reports on specific aspects of the project.',
        whyItMatters: 'Focused reports are more useful than data dumps. Stakeholders want relevant information.',
        whatToDo: 'Set appropriate filters: date range for the reporting period, status filters for active items, and priority for critical focus.',
      },
      {
        id: 'generate', target: null, roles: ['all'],
        title: 'Generate and Export',
        description: 'Generate reports on-screen and export as PDF for distribution.',
        whatIsThis: 'The process of creating and distributing project reports.',
        whyItMatters: 'Professional, timely reports build client confidence and demonstrate project control.',
        whatToDo: 'Review the on-screen report, make adjustments if needed, then export as PDF for distribution.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'Report Distribution',
        description: 'Share reports with project stakeholders via email or download.',
        whatIsThis: 'How reports reach their intended audience.',
        whyItMatters: 'Reports are only useful if they reach the right people at the right time.',
        whatToDo: 'Set up regular report distribution. Share executive summaries with leadership and detailed reports with project teams.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid outdated data and overly complex reports.',
        whatIsThis: 'Frequent reporting errors.',
        whyItMatters: 'Bad reports damage credibility and lead to poor decisions.',
        whatToDo: 'Always: (1) Ensure data is current before generating, (2) Keep reports focused and concise, (3) Distribute on schedule.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Module Connections',
        description: 'Reports aggregate data from all modules into a unified view.',
        whatIsThis: 'How reporting connects all project management functions.',
        whyItMatters: 'Cross-module reporting reveals project trends that individual module views cannot.',
        whatToDo: 'Use the Executive Dashboard for a high-level overview and module-specific reports for detailed analysis.',
      },
    ],
  },

  project_setup: {
    moduleId: 'project_setup',
    moduleName: 'Project Setup',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Project Setup',
        description: 'Create and configure new projects with all essential details.',
        whatIsThis: 'The starting point for every project in the system.',
        whyItMatters: 'A well-configured project ensures all modules work correctly from the start.',
        whatToDo: 'Learn what information to capture when setting up a new project.',
      },
      {
        id: 'basics', target: null, roles: ['all'],
        title: 'Project Basics',
        description: 'Enter: project name, client, contract type, location, and dates.',
        whatIsThis: 'The fundamental project information that appears across all modules.',
        whyItMatters: 'Accurate project details ensure correct reporting and communication.',
        whatToDo: 'Fill in all basic fields. Set the project start date, expected completion date, and contract value.',
      },
      {
        id: 'team', target: null, roles: ['all'],
        title: 'Project Team',
        description: 'Add team members with appropriate roles and permissions.',
        whatIsThis: 'The user access configuration for the project.',
        whyItMatters: 'Correct role assignment ensures team members can access what they need and nothing more.',
        whatToDo: 'Add team members. Assign roles: Admin, Contractor, Consultant. Set module permissions.',
        roleContent: {
          admin: { whatToDo: 'Add all team members, assign roles, and configure module-level permissions for each user.' },
        },
      },
      {
        id: 'config', target: null, roles: ['all'],
        title: 'Module Configuration',
        description: 'Configure module-specific settings like numbering formats and workflows.',
        whatIsThis: 'The settings that customise how each module works for this project.',
        whyItMatters: 'Proper configuration prevents confusion and ensures consistent document numbering.',
        whatToDo: 'Review and set: RFI numbering format, SI prefix, VO numbering, and other module-specific settings.',
      },
      {
        id: 'outputs', target: null, roles: ['all'],
        title: 'What Happens Next',
        description: 'After setup, the project is ready for all modules: delays, RFIs, drawings, programme, and more.',
        whatIsThis: 'The activation of all project modules after setup is complete.',
        whyItMatters: 'A properly set up project enables smooth workflow across all modules.',
        whatToDo: 'Start with: (1) Upload the programme, (2) Set up the IRS, (3) Upload initial drawings, (4) Begin logging correspondence.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid incomplete setup and incorrect role assignments.',
        whatIsThis: 'Setup errors that cause problems throughout the project lifecycle.',
        whyItMatters: 'Fixing setup errors mid-project disrupts workflows and causes data inconsistency.',
        whatToDo: 'Always: (1) Complete all required fields, (2) Verify role assignments, (3) Test access with a team member before going live.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Getting Started',
        description: 'After setup, explore each module through its guided walkthrough.',
        whatIsThis: 'The recommended sequence for onboarding into a new project.',
        whyItMatters: 'Following the recommended sequence ensures a smooth project launch.',
        whatToDo: 'Start with Programme Management, then IRS, then Drawing Register. Each module has its own guided tour.',
      },
    ],
  },

  executive_dashboard: {
    moduleId: 'executive_dashboard',
    moduleName: 'Executive Dashboard',
    steps: [
      {
        id: 'intro', target: null, roles: ['all'],
        title: 'Welcome to Executive Dashboard',
        description: 'A high-level overview of all project metrics across your entire portfolio.',
        whatIsThis: 'An aggregate view of key metrics from all modules across all projects.',
        whyItMatters: 'Executives need a quick, accurate overview without drilling into individual projects.',
        whatToDo: 'Understand how to read the dashboard and use it for decision-making.',
      },
      {
        id: 'metrics', target: '[data-testid="metrics-grid"]', roles: ['all'],
        title: 'Module Metrics',
        description: 'Each card shows the total and open/active count for a module across all projects.',
        whatIsThis: 'Aggregate counters for each project management module.',
        whyItMatters: 'High open counts indicate areas that need management attention.',
        whatToDo: 'Review each metric card. Focus on modules with high open counts that may indicate backlogs.',
      },
      {
        id: 'banner', target: '[data-testid="open-items-banner"]', roles: ['all'],
        title: 'Open Items Summary',
        description: 'The total count of all open/active items across all modules and projects.',
        whatIsThis: 'A single number that represents the total workload requiring attention.',
        whyItMatters: 'This number should trend downward as items are resolved. An increasing trend signals problems.',
        whatToDo: 'Monitor this number weekly. Investigate if it starts trending upward.',
      },
      {
        id: 'table', target: '[data-testid="project-breakdown"]', roles: ['all'],
        title: 'Project Breakdown',
        description: 'A detailed table showing open items by module for each project.',
        whatIsThis: 'A project-by-project view of where open items are concentrated.',
        whyItMatters: 'Identifies which projects need the most attention and in which areas.',
        whatToDo: 'Sort by open items to find the most demanding projects. Click the arrow to navigate to any project.',
      },
      {
        id: 'export', target: '[data-testid="download-pdf-btn"]', roles: ['all'],
        title: 'Export and Print',
        description: 'Download the dashboard as a PDF or print it for meetings and reports.',
        whatIsThis: 'The export functionality for sharing the dashboard view.',
        whyItMatters: 'Physical or PDF copies are useful for board meetings and stakeholder presentations.',
        whatToDo: 'Click PDF to download, or Print for a quick printout. Both produce clean, professional output.',
      },
      {
        id: 'mistakes', target: null, roles: ['all'],
        title: 'Common Mistakes',
        description: 'Avoid making decisions based on outdated data.',
        whatIsThis: 'Dashboard usage pitfalls.',
        whyItMatters: 'The dashboard is only as good as the data entered by project teams.',
        whatToDo: 'Click Refresh before important meetings. Ensure project teams are keeping their module data current.',
      },
      {
        id: 'links', target: null, roles: ['all'],
        title: 'Navigation',
        description: 'Click any project row to navigate directly to that project for detailed investigation.',
        whatIsThis: 'Quick navigation from the executive view to project-level detail.',
        whyItMatters: 'Fast drill-down enables executives to investigate issues immediately.',
        whatToDo: 'Use the dashboard as your starting point. Drill into projects that need attention, then into specific modules.',
      },
    ],
  },
};

export function getTourConfig(moduleId) {
  return TOUR_CONFIGS[moduleId] || null;
}

export function getAllModuleIds() {
  return Object.keys(TOUR_CONFIGS);
}

export default TOUR_CONFIGS;
